/**
 * BacktestLab — "What would I have made?" retrospective simulator.
 *
 * Pick a start date + initial capital + allocation across the 3 Core bots,
 * see what your portfolio would have done historically using REAL monthly
 * returns from each bot's MetaStats `monthlyAnalytics`.
 *
 * Compares the trio outcome vs equal-weight S&P benchmark (rough proxy).
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { TrendingUp, Calculator, Sparkles } from 'lucide-react';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { BOT_CONFIG } from './MTModule';

/* ────────── helpers ────────── */
const monthsBack = (n) => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCMonth(d.getUTCMonth() - n);
  return d;
};

const ymKey = (y, m) => `${y}-${m}`;

const buildBotSeries = (bot) => {
  // Map (year-month) → gain %
  const m = new Map();
  (bot.analytics?.monthlyAnalytics || []).forEach((row) => {
    const p = row.period || {};
    const y = p.year ?? row.year;
    const mo = p.month ?? row.month;
    if (y && mo) {
      const gain = Number(row.gain ?? row.gainPercent ?? row.profitPercent ?? 0);
      m.set(ymKey(y, mo), gain);
    }
  });
  return m;
};

const compoundedReturn = (botSeries, weights, startDate, endDate, capital) => {
  // Walk month-by-month from start to end
  const points = [{ ym: 'start', balance: capital }];
  let bal = capital;
  const d = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1));
  while (d < endDate) {
    const y = d.getUTCFullYear(), m = d.getUTCMonth() + 1;
    let weightedGain = 0;
    let totalW = 0;
    for (let i = 0; i < botSeries.length; i++) {
      const g = botSeries[i].get(ymKey(y, m));
      if (g !== undefined && weights[i] > 0) {
        weightedGain += (g / 100) * weights[i];
        totalW += weights[i];
      }
    }
    if (totalW > 0) bal = bal * (1 + weightedGain);
    points.push({ ym: ymKey(y, m), balance: bal });
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  return points;
};

const fmtUsd = (n) => {
  if (!isFinite(n)) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(0)}`;
};

/* ────────── allocation slider ────────── */
const AllocSlider = ({ bot, value, onChange, color, label }) => (
  <div className="flex items-center gap-3 mb-3">
    <div className="w-16 text-[10px] tracking-[0.2em] uppercase font-bold shrink-0" style={{ color }}>
      {label}
    </div>
    <div className="flex-1">
      <Slider value={[value]} onValueChange={(v) => onChange(v[0])} min={0} max={100} step={5} />
    </div>
    <div className="w-12 text-right text-sm font-bold tabular-nums font-mono" style={{ color }}>
      {value}%
    </div>
  </div>
);

/* ────────── equity curve sparkline ────────── */
const EquityCurve = ({ points, color }) => {
  if (!points || points.length < 2) return null;
  const W = 100, H = 30;
  const vals = points.map((p) => p.balance);
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const span = hi - lo || 1;
  const stepX = W / (points.length - 1);
  const coords = points.map((p, i) => [i * stepX, H - ((p.balance - lo) / span) * H]);
  const d = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const area = `${d} L ${W} ${H} L 0 ${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-12" preserveAspectRatio="none">
      <defs>
        <linearGradient id="bk-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.55" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#bk-grad)" />
      <path d={d} stroke={color} strokeWidth="1" fill="none" style={{ filter: `drop-shadow(0 0 2px ${color})` }} />
    </svg>
  );
};

/* ────────── presets ────────── */
const PRESETS = [
  { label: '1Y',  months: 12 },
  { label: '6M',  months: 6 },
  { label: '3M',  months: 3 },
  { label: 'YTD', months: new Date().getUTCMonth() + 1 },
];

const BacktestLab = ({ bots }) => {
  // Sort bots in canonical order
  const ordered = useMemo(() => {
    const ORDER = { sigmai: 0, sugair: 1, jairvis: 2 };
    return [...bots].sort((a, b) => (ORDER[a.bot] ?? 99) - (ORDER[b.bot] ?? 99));
  }, [bots]);

  const [capital, setCapital] = useState(1000);
  const [months, setMonths] = useState(12);
  const [alloc, setAlloc] = useState(
    Array(ordered.length).fill(0).map((_, i, arr) => Math.round(100 / arr.length))
  );

  // Normalize allocation (force sum=100)
  const updateAlloc = (idx, val) => {
    const next = [...alloc];
    next[idx] = val;
    const sum = next.reduce((s, x) => s + x, 0);
    if (sum !== 100 && next.length > 1) {
      // Distribute remainder among the others proportionally
      const others = next.map((v, i) => (i === idx ? 0 : v));
      const othersSum = others.reduce((s, x) => s + x, 0);
      const remainder = 100 - val;
      for (let i = 0; i < next.length; i++) {
        if (i === idx) continue;
        next[i] = othersSum > 0
          ? Math.max(0, Math.round((others[i] / othersSum) * remainder))
          : Math.max(0, Math.round(remainder / (next.length - 1)));
      }
    }
    setAlloc(next);
  };

  // Backtest computation
  const result = useMemo(() => {
    const botSeries = ordered.map(buildBotSeries);
    const weights = alloc.map((v) => v / 100);
    const start = monthsBack(months);
    const end = new Date();
    const points = compoundedReturn(botSeries, weights, start, end, capital);
    const final = points[points.length - 1].balance;
    const pnl = final - capital;
    const pctReturn = (pnl / capital) * 100;
    // Compute monthly avg
    const ret = points.length > 1 ? (Math.pow(final / capital, 1 / Math.max(1, months)) - 1) * 100 : 0;
    return { points, final, pnl, pctReturn, monthlyAvg: ret };
  }, [ordered, alloc, capital, months]);

  const positive = result.pnl >= 0;
  const lineColor = positive ? '#22c55e' : '#ef4444';

  return (
    <Card
      className="overflow-hidden border"
      style={{
        background: 'linear-gradient(180deg, rgba(0,244,255,0.04), rgba(4,8,22,0.85))',
        borderColor: 'rgba(0,244,255,0.30)',
        boxShadow: '0 18px 60px -25px rgba(0,244,255,0.35)',
      }}
      data-testid="backtest-lab"
    >
      <div className="px-5 py-3 flex items-center justify-between"
        style={{ background: 'rgba(0,244,255,0.05)', borderBottom: '1px solid rgba(0,244,255,0.18)' }}>
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-[#00f4ff]" />
          <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-white">Backtest Lab</h3>
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#00f4ff]">What would I have made?</span>
        </div>
      </div>

      <CardContent className="p-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT — controls */}
          <div className="space-y-4">
            {/* Capital input */}
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#9EB4C0] mb-1.5 block">
                Initial capital
              </label>
              <div className="flex items-center gap-2">
                <span className="text-[#9EB4C0] font-mono">$</span>
                <Input
                  type="number"
                  value={capital}
                  onChange={(e) => setCapital(Math.max(0, Number(e.target.value) || 0))}
                  className="bg-[#020817] border-[#00f4ff33] text-white font-mono"
                  data-testid="backtest-capital-input"
                />
              </div>
            </div>

            {/* Date range presets */}
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#9EB4C0] mb-1.5 block">
                Time range
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => setMonths(p.months)}
                    className="px-3 py-1.5 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase transition"
                    style={{
                      background: months === p.months ? '#00f4ff' : 'transparent',
                      color: months === p.months ? '#020817' : '#00f4ff',
                      border: '1px solid rgba(0,244,255,0.45)',
                      boxShadow: months === p.months ? '0 0 14px -2px rgba(0,244,255,0.5)' : 'none',
                    }}
                    data-testid={`backtest-range-${p.label}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Allocation sliders */}
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#9EB4C0] mb-2 block">
                Allocation (auto-balances to 100%)
              </label>
              {ordered.map((b, i) => {
                const cfg = BOT_CONFIG[b.bot] || { color: '#00f4ff', label: b.bot };
                return (
                  <AllocSlider key={b.bot}
                    bot={b.bot}
                    value={alloc[i]}
                    onChange={(v) => updateAlloc(i, v)}
                    color={cfg.color}
                    label={cfg.label || b.bot}
                  />
                );
              })}
            </div>
          </div>

          {/* RIGHT — result */}
          <div>
            <div className="rounded-lg p-4 mb-3"
              style={{
                background: `linear-gradient(165deg, ${lineColor}15 0%, rgba(4,8,22,0.7) 100%)`,
                border: `1px solid ${lineColor}55`,
                boxShadow: `0 10px 30px -15px ${lineColor}88`,
              }}>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#9EB4C0] mb-1">Final balance</div>
              <div className="text-3xl font-black tabular-nums font-mono" style={{ color: lineColor, textShadow: `0 0 14px ${lineColor}88` }}>
                {fmtUsd(result.final)}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <div>
                  <div className="text-[8px] tracking-[0.25em] uppercase text-[#9EB4C0]">P/L</div>
                  <div className="text-base font-bold tabular-nums font-mono" style={{ color: lineColor }}>
                    {positive ? '+' : ''}{fmtUsd(result.pnl)}
                  </div>
                </div>
                <div>
                  <div className="text-[8px] tracking-[0.25em] uppercase text-[#9EB4C0]">Total return</div>
                  <div className="text-base font-bold tabular-nums font-mono" style={{ color: lineColor }}>
                    {positive ? '+' : ''}{result.pctReturn.toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div className="text-[8px] tracking-[0.25em] uppercase text-[#9EB4C0]">Avg / month</div>
                  <div className="text-base font-bold tabular-nums font-mono text-white">
                    {result.monthlyAvg >= 0 ? '+' : ''}{result.monthlyAvg.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Equity curve */}
            <div className="rounded-lg p-3 mb-3"
              style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${lineColor}22` }}>
              <div className="text-[9px] tracking-[0.3em] uppercase text-[#9EB4C0] mb-1.5">Equity curve</div>
              <EquityCurve points={result.points} color={lineColor} />
            </div>

            {/* Take-away message */}
            <div className="rounded-lg p-3 flex items-start gap-2"
              style={{ background: 'rgba(0,244,255,0.06)', border: '1px solid rgba(0,244,255,0.30)' }}>
              <Sparkles className="w-4 h-4 mt-0.5 text-[#00f4ff] shrink-0" />
              <p className="text-[11px] text-[#cfd9e3] leading-snug">
                {positive
                  ? <>Past {months} month{months !== 1 ? 's' : ''}, this allocation would have <strong className="text-[#22c55e]">added {fmtUsd(result.pnl)}</strong> to your capital.</>
                  : <>This allocation underperformed in the chosen window — try increasing the most resilient bot&apos;s weight.</>}
              </p>
            </div>

            <p className="text-[9px] text-[#556475] italic mt-3 text-center">
              Backtest uses real MetaStats monthly returns. Past performance is not indicative of future results.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BacktestLab;
